import type {SelectOptionItem} from '@yandex-delivery/blocks'
import {
  FilterIcon,
  getHalfViewportSize,
  Grid,
  IconButton,
  isPointInsideMapBounds,
  Modal,
  Suggest,
  Typography,
  useMapRef,
} from '@yandex-delivery/blocks'
import {debounce, get, isNil} from '@yandex-delivery/utils'
import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {fetchAddressSuggest} from 'src/shared/api'
import {appConfig, i18n} from 'src/shared/config'
import {type LocationDetectedVariant, type PickupStation, DestinationPointDeliveryMethod} from 'src/shared/types'

import {PointItem} from './point-item/PointItem'
import {classes} from './PointsSidePanel.classes'
import {SelectCityModal} from './select-city-modal/SelectCityModal'
import type {Props} from './types'

export function PointsSidePanel(props: Props): React.ReactElement<Props> {
  const {
    address,
    points,
    pointType,
    selectedPoint,
    selectedPointCity,
    selectedFiltersAmount,
    onAddressSelect,
    onClose,
    onPointSelect,
    onCitySelect,
    onPointChoose,
    onOpenFilters,
  } = props
  const map = useMapRef()

  const [visiblePoints, setVisiblePoints] = useState<SelectOptionItem<PickupStation>[]>(points)

  const [isSelectCityModalVisible, setIsSelectCityModalVisible] = useState(false)

  const openCitySelectModalHandler = useCallback(() => {
    setIsSelectCityModalVisible(true)
  }, [])

  const closeCitySelectModalHandler = useCallback(() => {
    setIsSelectCityModalVisible(false)
  }, [])

  const applyCitySelectModalHandler = useCallback(
    (location?: LocationDetectedVariant) => {
      onCitySelect(location)
    },
    [onCitySelect],
  )

  const handleMapUpdate = useMemo(() => {
    if (!map) {
      return
    }

    return debounce(() => {
      const {size, zoom, center, projection} = map

      const globalCenter = projection.toWorldCoordinates([...center])
      const halfViewportSize = getHalfViewportSize(size, zoom)

      const top = globalCenter.y + halfViewportSize.y
      const bottom = globalCenter.y - halfViewportSize.y
      const left = globalCenter.x - halfViewportSize.x
      const right = globalCenter.x + halfViewportSize.x

      const pointsVisibleOnMap = points.filter(point => {
        const longitude = get('position.longitude', point.value)
        const latitude = get('position.latitude', point.value)

        if (isNil(longitude) || isNil(latitude)) {
          return false
        }

        return isPointInsideMapBounds(projection.toWorldCoordinates([longitude, latitude]), [top, bottom, left, right])
      })

      setVisiblePoints(pointsVisibleOnMap)
    }, 400)
  }, [map, points])

  useEffect(() => {
    handleMapUpdate?.()
  }, [handleMapUpdate, map?.bounds])

  return (
    <>
      <Modal.SidePanel
        anchor="right"
        backdrop={{
          className: classes.overlay,
        }}
        className={classes.root}
        onClose={onClose}
        size="s"
        enableFocusOutside
        opened
      >
        <Modal.Header
          py="m"
          renderTitle={() => (
            <Typography className={classes.header} group="normal" thickness="medium" variant="title4">
              <Grid wrap="wrap" container>
                <Grid item>{i18n(`ndd.${pointType}.plural_form`)} </Grid>
                <Grid item>
                  {i18n('ndd.prepositions.in', 'в')}&nbsp;
                  <span className={classes.selectCityButton} onClick={openCitySelectModalHandler}>
                    {selectedPointCity}
                  </span>
                </Grid>
              </Grid>
            </Typography>
          )}
          title={i18n(`ndd.${pointType}.plural_form`)}
        >
          <Grid className={classes.headerControls} size="s" wrap="nowrap" container>
            <Grid item xs>
              <Suggest
                defaultText={address?.text}
                fetchSuggest={fetchAddressSuggest}
                location={appConfig.location}
                onChange={onAddressSelect}
                placeholder={i18n('order.route.enter_address', 'Введите адрес')}
                pointType="a"
              />
            </Grid>
            {pointType === DestinationPointDeliveryMethod.PickUpPoint && (
              <Grid className={classes.filtersAmountWrap} item>
                <IconButton onPress={onOpenFilters}>
                  <FilterIcon />
                </IconButton>
                {selectedFiltersAmount > 0 && <div className={classes.filtersAmount}>{selectedFiltersAmount}</div>}
              </Grid>
            )}
          </Grid>
        </Modal.Header>
        <Modal.Body className={classes.body}>
          {visiblePoints.length === 0 && (
            <Typography className={classes.emptyText} color="minor" thickness="medium" variant="body2">
              {i18n(`ndd.${pointType}.empty_list`)}
            </Typography>
          )}

          {visiblePoints.length > 30 ? (
            <Grid alignItems="center" direction="column" size="xs">
              <Grid item>
                <Typography group="tight" justifyContent="center" thickness="medium" variant="body2">
                  {i18n(`ndd.${pointType}.select_address`)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography color="minor" group="tight" justifyContent="center" variant="caption1">
                  {i18n(`ndd.${pointType}.select_address.description`)}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid className={classes.itemList} direction="column" wrap="nowrap" container>
              {visiblePoints.map(point => (
                <Grid key={point.value.id} className={classes.item} item xs>
                  <PointItem
                    onPointChoose={onPointChoose}
                    onSelect={onPointSelect}
                    point={point.value}
                    pointType={pointType}
                    selectedPointId={selectedPoint?.id}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Modal.Body>
      </Modal.SidePanel>
      <SelectCityModal
        onClose={closeCitySelectModalHandler}
        onSelect={applyCitySelectModalHandler}
        open={isSelectCityModalVisible}
        selectedPointCity={selectedPointCity ?? ''}
      />
    </>
  )
}
